import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, Loader2, X, History, ChevronDown, ChevronRight, CheckCircle2, AlertCircle } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEMPLATES = [
  { id: 'status', label: 'Status update', text: "Quick update: your claim is progressing — we'll share more details as they come in." },
  { id: 'docs', label: 'Awaiting documents', text: "We're waiting on a few documents to complete the next step. Please send them at your earliest convenience." },
  { id: 'settle', label: 'Settlement offered', text: "A settlement offer has been entered on your claim. Please review and let us know how you'd like to proceed." },
  { id: 'closed', label: 'Claim closed', text: "Your claim has been closed. We'll be in touch shortly with any final paperwork." },
];

/**
 * Manual "Send Update" modal for the Edit Claim panel.
 * Calls POST /api/claims/{report_no}/notify and shows the delivery summary.
 */
export default function SendUpdateModal({ open, claim, companyRecipients = [], onClose, onSent }) {
  const [message, setMessage] = useState('');
  const [includeStatus, setIncludeStatus] = useState(true);
  const [includeRecentNotes, setIncludeRecentNotes] = useState(false);
  const [logAsNote, setLogAsNote] = useState(true);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadHistory = async () => {
    if (!claim?.report_no) return;
    setHistoryLoading(true);
    try {
      const res = await axios.get(
        `${API}/claims/${encodeURIComponent(claim.report_no)}/notifications`,
        { withCredentials: true }
      );
      setHistory(Array.isArray(res.data?.items) ? res.data.items : []);
    } catch {
      setHistory([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    if (open) loadHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, claim?.report_no]);

  const send = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message');
      return;
    }
    try {
      setSending(true);
      const res = await axios.post(
        `${API}/claims/${encodeURIComponent(claim.report_no)}/notify`,
        {
          message: message.trim(),
          include_status: includeStatus,
          include_recent_notes: includeRecentNotes,
          log_as_internal_note: logAsNote,
        },
        { withCredentials: true }
      );
      const j = res.data;
      if (j.skipped) {
        toast.warning(`Skipped: ${j.reason}`);
      } else if (j.failed > 0) {
        toast.warning(`Sent ${j.sent}/${j.sent + j.failed} — ${j.failed} delivery failure(s).`);
      } else {
        toast.success(`Update sent to ${j.sent} recipient${j.sent === 1 ? '' : 's'}`);
      }
      setMessage('');
      onSent?.(j);
      // Refresh history rather than closing immediately so the user sees the
      // new entry slot in.
      await loadHistory();
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send');
    } finally {
      setSending(false);
    }
  };

  const recipientCount = companyRecipients.length;
  const recipientPreview = companyRecipients.slice(0, 3).map((r) => r.email).join(', ');
  const remaining = recipientCount - 3;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl" data-testid="send-update-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Send className="w-5 h-5 text-teal-500" />
            Send update to customer
          </DialogTitle>
        </DialogHeader>

        {recipientCount === 0 ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            No recipients configured for <strong>{claim.company_name || 'this company'}</strong>. Add them on the Companies page before sending an update.
          </div>
        ) : (
          <div className="space-y-3">
            <div className="text-xs text-gray-500" data-testid="send-update-recipients">
              <span className="font-medium text-gray-700">To:</span>{' '}
              {recipientPreview}{remaining > 0 && <span className="text-gray-400"> +{remaining} more</span>}
              <span className="ml-2 text-gray-400">({recipientCount} total)</span>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Quick templates</label>
              <div className="flex flex-wrap gap-1.5">
                {TEMPLATES.map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => setMessage(t.text)}
                    className="px-2.5 py-1 text-xs rounded-full bg-gray-100 hover:bg-teal-100 hover:text-teal-700 text-gray-700 border border-gray-200"
                    data-testid={`template-${t.id}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-xs font-medium text-gray-600 mb-1.5 block">Message</label>
              <textarea
                rows={5}
                placeholder="Write a short update for the customer..."
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-teal-400 resize-vertical"
                data-testid="send-update-message"
              />
            </div>

            <div className="flex flex-col gap-2 text-xs">
              <label className="flex items-center gap-2">
                <Checkbox checked={includeStatus} onCheckedChange={setIncludeStatus} data-testid="send-update-include-status" />
                <span>Include current claim status in the email</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox checked={includeRecentNotes} onCheckedChange={setIncludeRecentNotes} data-testid="send-update-include-notes" />
                <span>Include last 3 internal notes</span>
              </label>
              <label className="flex items-center gap-2">
                <Checkbox checked={logAsNote} onCheckedChange={setLogAsNote} data-testid="send-update-log-note" />
                <span>Also log as an internal claim note (recommended for audit)</span>
              </label>
            </div>
          </div>
        )}

        {/* Notification history (collapsible) */}
        <div className="border-t border-gray-100 pt-3" data-testid="notification-history">
          <button
            type="button"
            onClick={() => setHistoryOpen((v) => !v)}
            className="w-full flex items-center gap-1.5 text-xs font-medium text-gray-600 hover:text-teal-600 transition-colors"
            data-testid="notification-history-toggle"
          >
            {historyOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            <History className="w-3.5 h-3.5" />
            <span>Notification history</span>
            <span className="ml-auto text-gray-400">
              {historyLoading ? '…' : `${history.length} sent`}
            </span>
          </button>
          {historyOpen && (
            <div className="mt-2 max-h-56 overflow-y-auto pr-1 space-y-1.5">
              {historyLoading && (
                <div className="text-xs text-gray-400 italic px-2 py-3 text-center">Loading…</div>
              )}
              {!historyLoading && history.length === 0 && (
                <div className="text-xs text-gray-400 italic px-2 py-3 text-center">
                  No notifications sent yet for this claim.
                </div>
              )}
              {history.map((n) => {
                const ok = (n.failed_count || 0) === 0 && (n.sent_count || 0) > 0;
                const when = (n.created_at || '').replace('T', ' ').slice(0, 16);
                const ev = (n.event || 'update').replace('_', ' ');
                return (
                  <div
                    key={n.notification_id}
                    className="bg-gray-50 border border-gray-200 rounded-md px-2.5 py-2 text-xs"
                    data-testid={`notification-history-item-${n.notification_id}`}
                  >
                    <div className="flex items-center gap-1.5">
                      {ok ? (
                        <CheckCircle2 className="w-3 h-3 text-teal-500 shrink-0" />
                      ) : (
                        <AlertCircle className="w-3 h-3 text-amber-500 shrink-0" />
                      )}
                      <span className="font-medium text-gray-700 capitalize truncate">{ev}</span>
                      {n.auto_fired && (
                        <span className="text-[9px] uppercase font-semibold px-1.5 py-0.5 rounded bg-blue-50 text-blue-600">Auto</span>
                      )}
                      <span className="ml-auto text-gray-400 text-[10px] whitespace-nowrap">{when}</span>
                    </div>
                    <div className="mt-1 text-gray-500 truncate">
                      {n.sent_count || 0}/{(n.sent_count || 0) + (n.failed_count || 0)} delivered
                      {n.recipients?.length > 0 && ` · ${n.recipients.slice(0, 2).join(', ')}${n.recipients.length > 2 ? ` +${n.recipients.length - 2}` : ''}`}
                    </div>
                    {n.message && (
                      <div className="mt-1 text-gray-600 italic line-clamp-2">"{n.message}"</div>
                    )}
                    {n.triggered_by_name && !n.auto_fired && (
                      <div className="mt-0.5 text-[10px] text-gray-400">by {n.triggered_by_name}</div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={sending} data-testid="send-update-cancel">
            <X className="w-4 h-4 mr-1" /> Cancel
          </Button>
          <Button
            onClick={send}
            disabled={sending || recipientCount === 0}
            className="bg-teal-500 hover:bg-teal-600"
            data-testid="send-update-submit"
          >
            {sending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
            Send Update
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
